import {
  List,
  ListItemButton,
  Radio,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  type InventoryResource,
  type RestoreAccount,
  type Provider,
  type Snapshot,
  AccountState,
} from '@repo/api-gw-sdk';
import { type Dispatch, type SetStateAction } from 'react';

import { AccountIndicator } from '@/components/accounts/accountIndicator';
import { Loader } from '@/components/layout/loading';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useIsAccountFeatureSupported } from '@/utils/useIsAccountFeatureSupported';

import { StepContainer } from '../../wizard/StepContainer';

const filterRestoreAccounts = (
  accounts: RestoreAccount[],
  supportedCloudProviders: Provider[]
) =>
  accounts.filter(
    (account) =>
      account.status === AccountState.ACCOUNT_STATE_CONNECTED &&
      account.isRestoreAllowed &&
      (!supportedCloudProviders ||
        supportedCloudProviders.includes(
          account.restoreAccountAttributes.cloudProvider
        ))
  );

const useIsRestoreAccountDisabled = () => {
  const { isDemo, isDev } = useEnvironment();
  const isAccountFeatureSupported = useIsAccountFeatureSupported();
  return (
    account: RestoreAccount,
    supportedInstalledVersion: string | undefined
  ): boolean =>
    !isDemo &&
    !isDev &&
    !!supportedInstalledVersion &&
    !isAccountFeatureSupported(supportedInstalledVersion, account);
};

interface RestoreAccountProps {
  supportedCloudProviders: Provider[];
  restoreAccount?: RestoreAccount;
  restoreToTime?: Date;
  isPiTR?: boolean;
  snapshot?: Snapshot;
  supportedInstalledVersion?: string;
  resource?: InventoryResource;
  sourceProvider?: Provider;
  sourceRegion?: string;
}

type SelectTargetAccountStepProps<T> = StepProps<T & RestoreAccountProps>;

export const SelectTargetAccountStep = <T,>(
  props: SelectTargetAccountStepProps<T>
) => {
  const dal = useDAL();
  const isRestoreAccountDisabled = useIsRestoreAccountDisabled();
  const { data: accountsBody } = dal.cloudAccounts.restore.useList();

  const restoreAccounts = filterRestoreAccounts(
    accountsBody?.accounts ?? [],
    props.wizardState.supportedCloudProviders
  );

  const sourceAccount = props.wizardState.resource?.providerAccountId
    ? restoreAccounts.find(
        (a) =>
          a.providerAccountId === props.wizardState.resource?.providerAccountId
      )
    : undefined;

  const isSourceAccountDisabled =
    sourceAccount !== undefined &&
    isRestoreAccountDisabled(
      sourceAccount,
      props.wizardState.supportedInstalledVersion
    );

  const effectiveRestoreAccount =
    props.wizardState.restoreAccount ??
    (isSourceAccountDisabled ? undefined : sourceAccount);

  const backupVaultInfo =
    props.wizardState.sourceProvider &&
    props.wizardState.sourceRegion !== undefined
      ? {
          cloudProvider: props.wizardState.sourceProvider,
          region: props.wizardState.sourceRegion,
        }
      : undefined;

  return (
    <StepContainer
      stepperLabels={props.stepperLabels}
      stepperIndex={Math.max(props.stepperLabels.indexOf('Restore Account'), 0)}
      onBackClick={props.back}
      canGoNext={() => !!effectiveRestoreAccount}
      onNextClick={async () => {
        if (effectiveRestoreAccount && !props.wizardState.restoreAccount) {
          props.setWizardState((state) => ({
            ...state,
            restoreAccount: effectiveRestoreAccount,
          }));
        }
        props.setNextStep(props.currentStep.next?.[0]);
      }}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot?.pointInTime}
          restoreToTime={props.wizardState.restoreToTime}
          vaultId={props.wizardState.snapshot?.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot?.accessDeniedItemsCount
          }
          supportPiTR={
            props.wizardState.isPiTR || props.wizardState.snapshot?.supportPiTR
          }
          backupVaultInfo={backupVaultInfo}
        />
      }
    >
      <SelectTargetAccount
        {...props.wizardState}
        restoreAccount={effectiveRestoreAccount}
        setRestoreAccountState={props.setWizardState}
      />
    </StepContainer>
  );
};

interface SelectTargetAccountProps<T> extends RestoreAccountProps {
  setRestoreAccountState: Dispatch<
    SetStateAction<
      T & {
        restoreAccount?: RestoreAccount;
      }
    >
  >;
  title?: string;
  accounts?: RestoreAccount[];
  emptyMessage?: string;
  isAccountDisabled?: (account: RestoreAccount) => boolean;
}

export const SelectTargetAccount = <T,>(props: SelectTargetAccountProps<T>) => {
  const dal = useDAL();
  const isRestoreAccountDisabled = useIsRestoreAccountDisabled();
  const { data: body } = dal.cloudAccounts.restore.useList();

  // Use provided accounts or fetch internally
  const accounts = props.accounts ?? body?.accounts ?? [];

  if (!props.accounts && !body) {
    return <Loader />;
  }

  const onRestoreAccountClicked = (account: RestoreAccount) => {
    props.setRestoreAccountState((state) => ({
      ...state,
      restoreAccount: account,
    }));
  };

  const filteredAccounts = filterRestoreAccounts(
    accounts,
    props.supportedCloudProviders
  );

  const getIsDisabled = (account: RestoreAccount): boolean =>
    props.isAccountDisabled
      ? props.isAccountDisabled(account)
      : isRestoreAccountDisabled(account, props.supportedInstalledVersion);

  return (
    <>
      <Typography component={'span'}>
        {props.title || 'Select an account to restore to:'}
      </Typography>
      {filteredAccounts.length === 0 && props.emptyMessage ? (
        // empty state message
        <Typography
          variant='subtitle2'
          color='text.secondary'
          className='mt-[20px]'
        >
          {props.emptyMessage}
        </Typography>
      ) : (
        <List className='mt-[20px] mb-[10px]'>
          {filteredAccounts.map((account) => {
            const isChecked = props.restoreAccount?.id === account.id;
            const isDisabled = getIsDisabled(account);
            return (
              <Tooltip
                key={account.id}
                title={
                  isDisabled
                    ? "This account doesn't have the required permissions. Please update the account's role"
                    : undefined
                }
              >
                <span>
                  <ListItemButton
                    key={account.id}
                    sx={{
                      border: isChecked
                        ? '2px solid var(--mui-palette-primary-main)'
                        : '1px solid var(--mui-palette-divider)',
                      padding: isChecked ? '16px 24px' : '17px 25px',
                      marginBottom: '8px',
                    }}
                    onClick={() => onRestoreAccountClicked(account)}
                    className='flex'
                    disabled={isDisabled}
                  >
                    <Radio
                      className='p-0'
                      checked={isChecked}
                      value={account.id}
                    />
                    <AccountIndicator account={account} copyable={false} />
                  </ListItemButton>
                </span>
              </Tooltip>
            );
          })}
        </List>
      )}
    </>
  );
};
